import type { ValidationSchema } from '@wix/editor-elements-corvid-utils';
import {
  composeSDKFactories,
  registerCorvidEvent,
  withValidation,
} from '@wix/editor-elements-corvid-utils';
import { createComponentSDKModel } from '@wix/editor-elements-integrations/corvid';
import type { CorvidSDKPropsFactory } from '@wix/editor-elements-types/corvid';
import { SdkMixin } from '@wix/lean-components-infra';
import type { SdkSchema } from '@wix/lean-components-infra/runtime/sdk';
import { createLeanComponentSDK } from '@wix/lean-components-infra/runtime/sdk';
import { cssVariables, defaultInputDisplay } from '../constants';
import type { ISelectableContainerProps } from '../types';
import type {
  ISelectableContainerCorvidProps,
  ISelectableContainerItemSDK,
  OnMouseEnterCorvidEvent,
  OnMouseEnterPayload,
  OnMouseLeaveCorvidEvent,
  OnMouseLeavePayload,
  OnSelectCorvidEvent,
  OnSelectPayload,
  SelectableContainerStylesSDK,
} from './SelectableContainer.corvid.types';

const sdkSchema: SdkSchema = {
  selected: {
    type: 'boolean',
  },
  inputType: {
    type: 'string',
  },
  inputLabel: {
    type: 'string',
  },
};

const validationSchema: ValidationSchema = {
  type: ['object'],
  properties: {
    selected: {
      type: ['boolean'],
    },
    inputType: {
      type: ['string'],
      enum: ['radio', 'checkbox'],
    },
    inputLabel: {
      maxLength: 120,
      minLength: 1,
      type: ['string'],
    },
  },
};

const stylesValidationSchema: ValidationSchema = {
  type: ['object'],
  properties: {
    inputVisible: {
      type: ['boolean'],
    },
  },
};

const mixins: Array<SdkMixin> = [SdkMixin.focusable];

const inputVisibleStylesSDK: CorvidSDKPropsFactory<
  ISelectableContainerProps,
  SelectableContainerStylesSDK
> = api => {
  const { createSdkState, setStyles } = api;
  const [state, setState] = createSdkState<{
    inputVisible: boolean | undefined;
  }>({ inputVisible: undefined }, 'SelectableContainer');

  return {
    get inputVisible() {
      return state.inputVisible;
    },
    set inputVisible(isVisible) {
      setState({ inputVisible: isVisible });
      setStyles({
        [cssVariables.corvidInputDisplay]: isVisible
          ? defaultInputDisplay
          : 'none',
      });
    },
  };
};

const itemEventsPropsSDKFactory: CorvidSDKPropsFactory<
  ISelectableContainerCorvidProps,
  ISelectableContainerItemSDK
> = api => ({
  onSelect: cb =>
    registerCorvidEvent<OnSelectPayload, OnSelectCorvidEvent>(
      'onSelect',
      api,
      cb,
      ({ componentEvent }) => componentEvent,
    ),

  onMouseEnter: cb =>
    registerCorvidEvent<OnMouseEnterPayload, OnMouseEnterCorvidEvent>(
      'onMouseEnter',
      api,
      cb,
      ({ componentEvent }) => componentEvent,
    ),

  onMouseLeave: cb =>
    registerCorvidEvent<OnMouseLeavePayload, OnMouseLeaveCorvidEvent>(
      'onMouseLeave',
      api,
      cb,
      ({ componentEvent }) => componentEvent,
    ),
});

export const sdk = composeSDKFactories([
  createLeanComponentSDK<ISelectableContainerProps>({
    sdkSchema,
    validationSchema,
    mixins,
  }),
  itemEventsPropsSDKFactory,
  withValidation(inputVisibleStylesSDK, stylesValidationSchema),
]);

export default createComponentSDKModel(sdk);
